<template>
  <div id="UserLogin">
    <h2 class="form-title">{{ getText.notLoggedIn.login.title }}</h2>
    <b-form class="mt-5 position-relative">
      <div class="loadingProgress" v-if="attemptingNonSsoLogin || loginTimedOut">
        <template v-if="attemptingNonSsoLogin">
          <div class="text-center loader-text">Checking Login Information...</div>
          <div class="loginLoader mx-auto mt-4"></div>
        </template>
        <template v-else-if="loginTimedOut">
          <img src="/img/wifi-slash.svg" alt="no wifi" class="d-block mx-auto" style="width: 30px">
          <div class="text-center mt-2 loader-text">Connection Lost. Network Error.</div>
        </template>
      </div>
      <b-form-group
          id="input-group-1"
          :label="getText.notLoggedIn.signUp.email"
          label-for="inputEmail"
      >
        <b-form-input
            type="email"
            id="inputEmail"
            v-model="user.email"
            :placeholder="getText.notLoggedIn.login.emailPlaceholder"
            :state="$v.user.email.$dirty ? !$v.user.email.$error : null"
            :disabled="isEmailDisabled"
            aria-describedby="user-name-error"
        ></b-form-input>
        <b-form-invalid-feedback id="user-name-error">
          {{ getText.notLoggedIn.login.usernameWarning }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group
          id="input-group-2"
          :label="getText.notLoggedIn.login.password"
          label-for="inputPass"
      >
        <div class="password-wrap">
          <b-form-input
              :type="passwordFieldType"
              id="inputPass"
              :placeholder="getText.notLoggedIn.login.passwordPlaceholder"
              v-model="user.password"
              :state="
                    $v.user.password.$dirty ? !$v.user.password.$error : null
                  "
              aria-describedby="password-error"
              @keyup.enter.native="submitForm"
          >

          </b-form-input>
          <div class="eye" @click="switchVisibility">
            <font-awesome-icon
                v-if="passwordFieldType === 'password'"
                icon="eye"
            ></font-awesome-icon>
            <font-awesome-icon
                v-else
                icon="eye-slash"
            ></font-awesome-icon>
          </div>
        </div>
        <b-form-invalid-feedback id="password-error">
          {{ getText.notLoggedIn.login.passwordWarning }}
        </b-form-invalid-feedback>
      </b-form-group>
      <b-form-group
      v-if="BOAClients && BOAClients.length"
      id="clientList"
      :label="getText.notLoggedIn.login.BOAClients"
      label-for="clientSelect"
      >
      <div class="client-select">
          <b-form-select
            id="clientSelect"
            v-model="user.client"
            class="form-control"
            required
          >
            <option :value="null" disabled>Please Select...</option>
            <option
              v-for="client in BOAClients"
              :value="client.client_id"
              >{{ client.client_name }}</option
            >
          </b-form-select>
          <i class="fa fa-chevron-down" aria-hidden="true"></i>
        </div>
        </b-form-group>
      <div class="forgot-password" @click="$emit('forgotPassword')">
        <span>{{
          getText.notLoggedIn.login.forgotPassword
        }}</span>
      </div>
      <div class="row mt-5 d-flex align-items-center justify-content-center">
        <div
            class="
                submit-form
                text-center
                d-flex
                align-items-center
                justify-content-center
                col-6
              "
        >
          <b-button
              type="button"
              @click="submitForm"
              class="py-3 sign-in-button"
              :class="{invalid: $v.user.$invalid}"
              variant="cta"
              :disabled="$v.user.$invalid || !isBOAClientSelected || isSignInLoading"
          ><span></span>
            <span>
              {{ buttonText }}
            </span>
            <i class="fa fa-arrow-right" aria-hidden="true"></i>
          </b-button>
        </div>
      </div>
      <div class="newUserText" @click="switchForm"><span class="newUserSpan">{{ getText.notLoggedIn.login.newUserText }}</span></div>
    </b-form>
    <div class="orDivider" v-if="showGoogleSso || showMsSso">
      <div class="divider"></div>
      <div class="or">or</div>
      <div class="divider"></div>
    </div>
    <ThirdPartyLogin v-if="showGoogleSso || showMsSso" :show-google-sso="showGoogleSso" :show-ms-sso="showMsSso"/>
    <div class="smallPrint">Protected by reCAPTCHA and subject to the <a :href="privacyPolicyLink" target="_blank">Privacy
      Policy</a> and <a href="/terms-and-conditions" target="_blank">Terms of Service</a>.</div>
  </div>
</template>

<script>
import LanguageSelect from "@/components/LanguageSelect.vue";
import {validationMixin} from "vuelidate";
import {email, required} from "vuelidate/lib/validators";
import axios from "axios";
import config from "@/config/constants";
import {mapGetters} from "vuex";
import ThirdPartyLogin from "@/components/NotLoggedIn/ThirdPartyLogin.vue";
import EventBus from "../../helpers/eventbus";
import { isAustralianApiUrl } from "@journeys/utils/helpers";

export default {
    name: "UserLogin",
    components: {LanguageSelect, ThirdPartyLogin},
    props: [],
    mixins: [validationMixin],
    data() {
      return {
        user: {
          email: "",
          password: "",
          client: null
        },
        newUser: {
          org_code: "4wSZAoYA",
        },
        isSignUp: false,
        organisation: null,
        passwordFieldType: "password",
        preventSso: true,
        attemptingNonSsoLogin: false,
        loginTimedOut: false,
        BOAClients: [],
        referralId: null,
        referralData: null,
        isEmailDisabled: false,
        isSignInLoading: false
      };
    },
    mounted() {
      EventBus.$on('login-time-out',this.handleTimeout);
      EventBus.$on('login-unsuccessful',this.handleFailedLogin)
      EventBus.$on('login-boa', this.handleBOALogin)

      if (this.$route.query.referral) {
        const code = this.$route.query.referral;
        const referralString = localStorage.getItem('referral');

        if (referralString) {
          this.referralData = JSON.parse(referralString);
            if (this.referralData && this.referralData.email_address) {
              this.user.email = this.referralData.email_address;
              this.isEmailDisabled = true;
            }
          }

        try {
          const decoded = atob(code);

          this.referralId = JSON.parse(decoded).referral_id;
        } catch (err) {
          console.error("Invalid referral code", err);
        }
      }
    },
    validations: {
      user: {
        email: {
          required,
          email
        },
        password: {
          required,
        },
      },
    },
    computed: {
    ...mapGetters(["hardcoded", "skin","client","darkMode"]),
    ...mapGetters(["hardcoded", "skin","client"]),
    buttonText() {
      if(this.loginTimedOut) return 'Try again';
      if(this.attemptingNonSsoLogin) return 'Checking details...'
      return this.getText.notLoggedIn.login.buttonText;
    },
    showGoogleSso() {
      return this.client.google_sso_enabled === 1 && !this.preventSso;
    },
    showMsSso() {
      return this.client.ms_sso_enabled === 1 && !this.preventSso;
    },
    isBOAClientSelected() {
      return this.BOAClients && this.BOAClients.length ? this.user.client : true
    },
    // AUS builds link to the in-app policy page; all others use the marketing site.
    privacyPolicyLink() {
      return isAustralianApiUrl(process.env.VUE_APP_API_PATH)
        ? '/privacy-policy'
        : 'https://www.spectrum.life/privacy/';
    }
  },
    methods: {
      getLogo() {
        if (this.hardcoded && this.hardcoded.client_logo) {
          return this.hardcoded.client_logo;
        } else if (this.skin === 8) {
          return "/img/boi/boi-logo-alt.png";
        } else {
          if(this.client.client_logo === "https://healthcoach-media.com/res/images/esp/customise/spectrum-life-logo.png") {
            return this.darkMode ? this.defaultDarkLogo : this.defaultLightLogo;
          }
          return this.client.client_logo
        }
      },
      handleFailedLogin() {
        this.loginTimedOut = false;
        this.attemptingNonSsoLogin = false;
      },
      handleTimeout() {
        this.loginTimedOut = true;
        this.attemptingNonSsoLogin = false;
      },
      handleBOALogin(BOAClients) {
        this.loginTimedOut = false;
        this.attemptingNonSsoLogin = false;
        this.BOAClients = BOAClients
      },
      switchForm() {
        this.$emit('switchForm');
      },
      validateState(ref) {
        if (
            this.veeFields[ref] &&
            (this.veeFields[ref].dirty || this.veeFields[ref].validated)
        ) {
          return !this.veeErrors.has(ref);
        }
        return null;
      },
      submitForm() {
          this.$v.user.$touch();
          if (this.$v.user.$anyError && this.isBOAClientSelected) {
            return;
          }
        this.attemptingNonSsoLogin = true;
        this.signIn();
      },
      async signIn() {
        if (this.isSignInLoading) {
          return
        }
        try {
          this.isSignInLoading = true
          let payload = {
            username: this.user.email,
            password: this.user.password,
            encrypt_level: "high",
            platform: "web",
            lang: this.$store.state.lang,
          };
          if(this.referralId) {
            payload["referral_id"] = this.referralId
          }
          if (this.user?.client) {
            payload.client_id = this.user?.client
          }
          let v3_ready = await this.$store.dispatch("signIn", {
            router: this.$router,
            payload,
            newLogin: true
          });
          if (v3_ready === false) {
            this.$emit("not-v3");
          }
        } catch(err) {
          console.error(err)
        } finally {
          this.isSignInLoading = false
        }
      },
      async getOrgLocations() {
        let payload = { org_code: "4wSZAoYA", lang: this.$store.state.lang };
        try {
          let res = await axios.post(
              config.api_env + "/application/api-hc/get-client-signup-options",
              payload,
              config.options
          );
          this.organisation = res.data;
        } catch (e) {
          console.log(e);
        }
      },
      switchVisibility() {
        this.passwordFieldType =
            this.passwordFieldType === "password" ? "text" : "password";
      },
    },
    watch: {
      'user.email'(newValue) {
        const atIndex = newValue.indexOf('@');
        if (atIndex !== -1) {
          this.user.email = newValue.substring(0, atIndex + 1) + newValue.substring(atIndex + 1).toLowerCase();
        }
      },
      loginTimedOut(val) {
        if(val) {
          setTimeout(()=> {
            this.loginTimedOut = false;
          },5000)
        }
      }
    }
}

</script>
<style lang="css">
.loginLoader {
  border-radius: 5px;
  width: 80%;
  height: 10px;
  background:
      linear-gradient(var(--brand-primary)) 0/0 no-repeat
      #BCB2FE;
  animation: l1 15s infinite linear;
}
@keyframes l1 {
  100% {background-size:100%}
}
</style>
<style lang="less">
  #UserLogin {
    width: 100%;
    height: 100%;
    padding: 40px 100px 20px;
    border-radius: 10px;
    .loader-text {
      font-family: "DMSans"
    }
    .form-group {
      margin-bottom: 2rem;
    }
    .sign-in-button {
      display: flex;
      align-items: center;
      justify-content: space-between;
      padding: 12px !important;
      height: auto !important;
      min-width: 280px;
      .fa-arrow-right {
        background-color: #fff;
        color: #122130;
        border-radius: 5px;
        padding: 10px;
      }
    }
    .loadingProgress {
      border-radius: 10px;
      width: calc(100% + 20px);
      position: absolute;
      left: -10px;
      height: 150px;
      background-color: #D0D3D6;
      opacity: 0.9;
      z-index: 9999;
      display: flex;
      flex-direction: column;
      font-size: 1.6rem;
      font-weight: bold;
      justify-content: center;
    }
    .logoContainer {
      height: 100px;
      width: 100%;
      img {
        float: right;
        width: 40%;
        margin-right: 10px;
      }
    }
    .form-title {
      font-size: 3.6rem;
      color: #122130;
      text-align: center;
      font-family: "Raptor V3 Black";
      margin-bottom: 60px;
    }

    label {
      font-size: 1.4rem;
    }
    .password-wrap {
      position: relative;
      .eye {
        cursor: pointer;
        position: absolute;
        right: 20px;
        top: 50%;
        transform: translateY(-50%);
        font-size: 18px;
      }
    }
    .newUserSpan {
      font-size: 15px;
      font-family: "DMSans Bold";
    }
    .forgot-password {
      font-size: 1.5rem;
      font-family: DMSans, sans-serif;
      text-decoration: underline;
      color: #122130;
      cursor: pointer;
    }
    .invalid-feedback {
      font-size: 1.3rem;
    }
    .orDivider {
      margin: 25px 0 15px 0;
      align-items: center;
      justify-content: center;
      display: flex;

      .divider {
        height: 1px;
        width: 43%;
        background: #DCDBDD;
      }
      .or {
        width: 14%;
        text-align: center;
        font-size: 1.5rem;
      }
    }
    .btn-cta {
      width: 75%;
      margin: auto;
      height: 50px;
      border-radius: 15px;
      font-size: 1.6rem;
      &:active &:not(.invalid) {
        transform: translateY(2px);
      }
      &.invalid {
        background: grey !important;
      }
    }
    .client-select {
      position: relative;
      font-size: 2rem;

      .fa-chevron-down {
        position: absolute;
        top: 50%;
        transform: translateY(-50%);
        right: 15px;
      }

      .custom-select {
        cursor: pointer !important;
      }
    }
    .smallPrint {
      text-align: center;
      margin-top: 40px;
      font-size: 1.6rem;
      a {
        background: linear-gradient(90deg, #ED3A75 0%, #6A356A 100%);
        -webkit-background-clip: text;
        -webkit-text-fill-color: transparent;
        cursor: pointer;
        &:hover {
          background: linear-gradient(90deg, #6A356A 0%, #ED3A75 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          cursor: pointer;
        }
      }
    }
  }
</style>